import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { SURFACE_RECIPES } from '../entities/recipe/data/initialData';
import { Recipe, Service, RecipeLayer } from '@/shared/model';

export type RecipeCategoryMap = Record<string, Recipe[]>;

function applyUpdater<T>(updater: T | ((prev: T) => T), prev: T): T {
  return typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
}

interface RecipeState {
  recipes: RecipeCategoryMap;
  setRecipes: (recipesOrUpdater: RecipeCategoryMap | ((prev: RecipeCategoryMap) => RecipeCategoryMap)) => void;
  createNewRecipe: (category: string, defaultName?: string) => Recipe;
  deleteRecipe: (category: string, id: string) => boolean;
  updateRecipe: (category: string, id: string, updates: Partial<Recipe>) => void;
  updateRecipeName: (category: string, id: string, newName: string) => void;
  addLayerToRecipe: (recipeId: string, recipeCategory: string, service: Service, coefficient?: number) => void;
  updateRecipeLayer: (recipeId: string, recipeCategory: string, index: number, field: keyof RecipeLayer, value: RecipeLayer[keyof RecipeLayer]) => void;
  moveRecipeLayer: (recipeId: string, recipeCategory: string, index: number, direction: 'up' | 'down') => void;
  removeLayerFromRecipe: (recipeId: string, recipeCategory: string, index: number) => void;
  resetRecipeData: () => void;
}

export const useRecipeStore = create<RecipeState>()(
  persist(
    (set) => ({
      recipes: SURFACE_RECIPES as RecipeCategoryMap,

      setRecipes: (recipesOrUpdater) => set((state) => ({
        recipes: applyUpdater(recipesOrUpdater, state.recipes),
      })),

      createNewRecipe: (category, defaultName = 'Новый шаблон') => {
        const newRecipe: Recipe = {
          id: `pie_${category}_${Date.now()}`,
          name: defaultName,
          layers: [],
        };
        set((state) => ({
          recipes: {
            ...state.recipes,
            [category]: [...(state.recipes[category] || []), newRecipe],
          },
        }));
        return newRecipe;
      },

      deleteRecipe: (category, id) => {
        set((state) => ({
          recipes: {
            ...state.recipes,
            [category]: (state.recipes[category] || []).filter((r) => r.id !== id),
          },
        }));
        return true;
      },

      updateRecipe: (category, id, updates) => set((state) => ({
        recipes: {
          ...state.recipes,
          [category]: (state.recipes[category] || []).map((r) => r.id === id ? { ...r, ...updates } : r),
        },
      })),

      updateRecipeName: (category, id, newName) => set((state) => ({
        recipes: {
          ...state.recipes,
          [category]: (state.recipes[category] || []).map((r) => r.id === id ? { ...r, name: newName } : r),
        },
      })),

      addLayerToRecipe: (recipeId, recipeCategory, service, coefficient = 1) => set((state) => ({
        recipes: {
          ...state.recipes,
          [recipeCategory]: (state.recipes[recipeCategory] || []).map((r) => {
            if (r.id !== recipeId) return r;
            return { ...r, layers: [...r.layers, { serviceId: service.id, coefficient }] };
          }),
        },
      })),

      updateRecipeLayer: (recipeId, recipeCategory, index, field, value) => set((state) => ({
        recipes: {
          ...state.recipes,
          [recipeCategory]: (state.recipes[recipeCategory] || []).map((r) => {
            if (r.id !== recipeId) return r;
            const newLayers = [...r.layers];
            newLayers[index] = { ...newLayers[index], [field]: value } as RecipeLayer;
            return { ...r, layers: newLayers };
          }),
        },
      })),

      moveRecipeLayer: (recipeId, recipeCategory, index, direction) => set((state) => ({
        recipes: {
          ...state.recipes,
          [recipeCategory]: (state.recipes[recipeCategory] || []).map((r) => {
            if (r.id !== recipeId) return r;
            const newLayers = [...r.layers];
            if (direction === 'up' && index > 0) {
              [newLayers[index], newLayers[index - 1]] = [newLayers[index - 1], newLayers[index]];
            } else if (direction === 'down' && index < newLayers.length - 1) {
              [newLayers[index], newLayers[index + 1]] = [newLayers[index + 1], newLayers[index]];
            }
            return { ...r, layers: newLayers };
          }),
        },
      })),

      removeLayerFromRecipe: (recipeId, recipeCategory, index) => set((state) => ({
        recipes: {
          ...state.recipes,
          [recipeCategory]: (state.recipes[recipeCategory] || []).map((r) => {
            if (r.id !== recipeId) return r;
            const newLayers = [...r.layers];
            newLayers.splice(index, 1);
            return { ...r, layers: newLayers };
          }),
        },
      })),

      resetRecipeData: () => set({ recipes: SURFACE_RECIPES as Record<string, Recipe[]> }),
    }),
    {
      name: 'estimatix-recipes',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);